import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Topbar from "../../components/Topbar/Topbar";
import { logActivity } from "../../utils/activity";
import { fetchRooms, createReport, fetchReport, CATEGORY_LABELS, SEVERITY_LABELS } from "./api";
import { PhotoGrid } from "./FaultReportsPage";
import UploadQR from "./UploadQR";
import "./FaultReports.css";

const CATEGORIES = ["prop", "teknik", "lokale", "andet"];
const SEVERITIES = ["lav", "normal", "hoej", "kritisk"];

// NewFaultReportPage is the form a GM fills when something is broken, and — once the ticket
// exists — the QR code their phone scans to add photos. Creating the ticket is the moment the
// Teams card goes out, so the form asks for the essentials only: room, category, severity and a
// description. Photos are never required.
export default function NewFaultReportPage() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [rooms, setRooms] = useState([]);
    const [form, setForm] = useState({
        game_id: params.get("game") || "",
        category: "prop",
        severity: "", // no default: the GM must take an active stand on how serious the fault is
        title: "",
        description: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [created, setCreated] = useState(null); // { key, url, upload_token, expires_at }

    useEffect(() => {
        logActivity("open_new_fault_report");
        fetchRooms()
            .then(d => {
                setRooms(d.rooms || []);
                setForm(f => f.game_id || !(d.rooms || []).length ? f : { ...f, game_id: d.rooms[0].game_id });
            })
            .catch(e => setError(/503/.test(e.message) ? "Fejlmeldinger er ikke sat op på serveren." : "Kunne ikke hente rum."));
    }, []);

    const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

    async function submit(e) {
        e.preventDefault();
        if (!form.game_id || !form.severity || !form.description.trim()) return;
        setSubmitting(true);
        setError("");
        try {
            const out = await createReport(form);
            logActivity("create_fault_report");
            setCreated({ ...out, title: form.title.trim() || form.description.trim().split(/\r?\n/)[0] });
        } catch (err) {
            setError(/502/.test(err.message) ? "Serveren svarede ikke. Prøv igen om lidt." : `Kunne ikke oprette fejlmeldingen. ${err.message}`);
        } finally {
            setSubmitting(false);
        }
    }

    const room = rooms.find(r => r.game_id === form.game_id);

    return (
        <>
            <Topbar />
            <div className="fr-content fr-narrow">
                {created ? (
                    <CreatedView created={created} room={room} onAnother={() => { setCreated(null); setForm(f => ({ ...f, title: "", description: "" })); }} onList={() => navigate("/fejlmeldinger")} />
                ) : (
                    <form className="fr-card fr-form" onSubmit={submit}>
                        <h1>Ny fejlmelding</h1>
                        <p className="fr-sub">Jo mere præcist du beskriver fejlen, jo hurtigere kan den fikses. Billeder tager du med telefonen på den næste side.</p>

                        <label className="fr-field">
                            <span>Rum</span>
                            <select value={form.game_id} onChange={set("game_id")} required>
                                {rooms.length === 0 && <option value="">Henter rum…</option>}
                                {rooms.map(r => <option key={r.game_id} value={r.game_id}>{r.name} · {r.location}</option>)}
                            </select>
                        </label>

                        <div className="fr-field">
                            <span>Kategori</span>
                            <div className="fr-segmented">
                                {CATEGORIES.map(c => (
                                    <button type="button" key={c} className={form.category === c ? "active" : ""} onClick={() => setForm(f => ({ ...f, category: c }))}>
                                        {CATEGORY_LABELS[c]}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="fr-field">
                            <span>Alvorlighed <em>(vælg én)</em></span>
                            <div className="fr-segmented">
                                {SEVERITIES.map(s => (
                                    <button type="button" key={s} className={`${form.severity === s ? "active" : ""} fr-sev-${s}`} onClick={() => setForm(f => ({ ...f, severity: s }))}>
                                        {SEVERITY_LABELS[s]}
                                    </button>
                                ))}
                            </div>
                            <ul className="fr-sev-legend">
                                <li><b>Kritisk</b> rummet kan ikke spilles</li>
                                <li><b>Høj</b> spillet påvirkes</li>
                                <li><b>Normal</b> skal fikses snart</li>
                                <li><b>Lav</b> kosmetisk</li>
                            </ul>
                        </div>

                        <label className="fr-field">
                            <span>Overskrift <em>(valgfri)</em></span>
                            <input type="text" maxLength={80} value={form.title} onChange={set("title")} placeholder="Kort, fx 'Kuffertlås hænger'" />
                        </label>

                        <label className="fr-field">
                            <span>Beskrivelse</span>
                            <textarea rows={5} value={form.description} onChange={set("description")} required
                                placeholder="Hvad er galt, hvor i rummet, og hvad skete der? Skriv også hvis du har lavet en midlertidig løsning." />
                        </label>

                        {error && <div className="fr-error">{error}</div>}

                        <div className="fr-actions">
                            <button type="button" className="fr-ghost" onClick={() => navigate("/fejlmeldinger")}>Annullér</button>
                            <button type="submit" className="fr-primary" disabled={submitting || !form.game_id || !form.severity || !form.description.trim()}>
                                {submitting ? "Et øjeblik…" : "Næste"}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </>
    );
}

// CreatedView shows the QR code for the phone and the photos as they land. It polls the issue
// every few seconds while open so a photo shot on the phone appears here without a refresh.
function CreatedView({ created, room, onAnother, onList }) {
    const [photos, setPhotos] = useState([]);

    useEffect(() => {
        let alive = true;
        const poll = async () => {
            try {
                const d = await fetchReport(created.key);
                if (alive) setPhotos(d.photos || []);
            } catch { /* keep the last list */ }
        };
        poll();
        const timer = setInterval(poll, 4000);
        return () => { alive = false; clearInterval(timer); };
    }, [created.key]);

    return (
        <div className="fr-card fr-created">
            <div className="fr-created-head">
                <div className="fr-check">✓</div>
                <div>
                    <h1>Fejlmeldingen er oprettet</h1>
                    <p className="fr-created-title">{created.title}</p>
                    <p className="fr-sub">{room ? `${room.name} · ${room.location} · ` : ""}nr. {created.key}</p>
                </div>
            </div>

            <UploadQR issueKey={created.key} title={created.title} initialToken={created.upload_token} initialExpiresAt={created.expires_at} />

            <div className="fr-photos-head">
                <span className="fr-label">Billeder ({photos.length})</span>
            </div>
            <PhotoGrid photos={photos} emptyText="Venter på billeder fra telefonen…" />

            <div className="fr-actions">
                <button className="fr-ghost" onClick={onAnother}>Ny fejlmelding</button>
                <button className="fr-primary" onClick={onList}>Færdig</button>
            </div>
        </div>
    );
}
